import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiUser,
  FiTarget,
  FiCpu,
  FiLayers,
  FiActivity,
  FiBookOpen,
  FiPieChart,
  FiPrinter,
  FiArrowRight,
  FiArrowLeft,
  FiX,
  FiCheck,
  FiInfo
} = FiIcons;

const steps = [
  {
    title: "Step 1: Add Your Profile",
    description: "Begin your financial journey by setting up your personal profile. This helps us understand your financial situation and risk appetite.",
    details: [
      "Enter your current age",
      "Set your monthly income (optional)",
      "Define existing investments (optional)",
      "Most importantly: Choose your Risk Comfort level (Low/Medium/High)",
      "Use the 'Analyze Risk' button for a guided quiz to determine your risk profile"
    ],
    icon: FiUser,
    color: "bg-blue-100 text-blue-600",
    tip: "Your risk profile determines which fund categories we'll recommend for your goals.",
    route: '/',
    scrollTo: 'profile-section'
  },
  {
    title: "Step 2: Add Your Financial Goals",
    description: "Define what you're saving for. Each goal should have a clear target and timeline.",
    details: [
      "Click the 'Add Goal' button",
      "Select from preset goals (Education, Retirement, House, etc.) or create custom ones",
      "Enter the cost in today's terms",
      "Set how many years away the goal is",
      "Input your expected return percentage",
      "Optionally: Mark if you're already investing for this goal"
    ],
    icon: FiTarget,
    color: "bg-red-100 text-red-600",
    tip: "Be realistic with costs. We automatically adjust for inflation based on goal type.",
    route: '/',
    scrollTo: 'goals-section'
  },
  {
    title: "Step 3: Click 'Simulate Results'",
    description: "Once you've added all your goals, it's time to see the magic happen!",
    details: [
      "Review all your goals one final time",
      "Ensure your profile is complete",
      "Click the 'Simulate Results' button at the bottom",
      "Our engine will process your data instantly",
      "You'll be taken to a comprehensive results dashboard"
    ],
    icon: FiCpu,
    color: "bg-purple-100 text-purple-600",
    tip: "You can always go back to edit goals or profile information later.",
    route: '/',
    scrollTo: 'simulate-button'
  },
  {
    title: "Step 4: Goal Financial Summary",
    description: "The first thing you'll see is a comprehensive financial summary table showing all your goals at a glance.",
    details: [
      "View each goal's future cost (inflation-adjusted)",
      "See required monthly SIP for each goal",
      "Check one-time lumpsum investment alternatives",
      "Time horizons are color-coded (Short/Medium/Long term)",
      "Inflation rates are shown for each goal"
    ],
    icon: FiLayers,
    color: "bg-indigo-100 text-indigo-600",
    tip: "This table gives you the big picture of your total financial commitment.",
    route: '/results',
    scrollTo: 'goal-summary-table'
  },
  {
    title: "Step 5: Individual Goal Analysis",
    description: "Scroll down to see detailed analysis for each individual goal. This is where we show you the 'Cost of Wrong Choices'.",
    details: [
      "Each goal gets its own analysis card",
      "See 'Right Choice' vs 'Wrong Choice' fund categories",
      "Understand how choosing the wrong fund affects your timeline",
      "View potential delays or extra costs",
      "Toggle between SIP and Lumpsum views",
      "Edit expected returns on the fly to see impact"
    ],
    icon: FiActivity,
    color: "bg-orange-100 text-orange-600",
    tip: "Short-term goals in equity = high risk. Long-term goals in debt = low returns!",
    route: '/results',
    scrollTo: 'individual-goals'
  },
  {
    title: "Step 6: Fund Category Reference Guide",
    description: "A comprehensive reference table showing which mutual fund categories are suitable for different time horizons.",
    details: [
      "Organized by investment horizon (< 3 years, 3-5 years, etc.)",
      "Three risk profiles: Conservative, Moderate, Aggressive",
      "Specific fund category recommendations for each combination",
      "Color-coded for easy understanding",
      "Use this as a quick reference when selecting actual funds"
    ],
    icon: FiBookOpen,
    color: "bg-teal-100 text-teal-600",
    tip: "This table is gold! Bookmark it or print it for future reference.",
    route: '/results',
    scrollTo: 'fund-reference-table'
  },
  {
    title: "Step 7: Goal-Aligned Portfolio",
    description: "Get a personalized fund allocation strategy built bottom-up from your specific goals.",
    details: [
      "See exactly how much to invest in each fund category",
      "Allocation percentages are calculated from goal timelines",
      "Each category shows which goals it supports",
      "View time horizons for each allocation",
      "Edit and customize the table if needed",
      "Reset to recommended allocation anytime"
    ],
    icon: FiPieChart,
    color: "bg-green-100 text-green-600",
    tip: "Unlike generic 60:40 portfolios, this is built specifically for YOUR goals.",
    route: '/results',
    scrollTo: 'goal-aligned-portfolio'
  },
  {
    title: "Step 8: Print & Save Your Plan",
    description: "Finally, save your comprehensive financial plan as a PDF for future reference or to discuss with your advisor.",
    details: [
      "Click the 'Print / Save PDF' button at the top",
      "Your browser's print dialog will open",
      "Choose 'Save as PDF' as the destination",
      "All charts, tables, and analysis will be included",
      "Interactive elements are hidden for clean printing",
      "Share with your financial advisor or keep for records"
    ],
    icon: FiPrinter,
    color: "bg-gray-100 text-gray-600",
    tip: "Pro tip: Save multiple versions as you adjust your goals over time to track progress.",
    route: '/results',
    scrollTo: 'print-button'
  }
];

const Walkthrough = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add highlight effect
        element.classList.add('highlight-pulse');
        setTimeout(() => {
          element.classList.remove('highlight-pulse');
        }, 2000);
      }
    }, 300);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Navigate to the route and scroll to section
      const step = steps[nextStep];
      navigate(step.route);
      scrollToSection(step.scrollTo);
    } else {
      onClose();
      setTimeout(() => setCurrentStep(0), 300);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      
      // Navigate to the route and scroll to section
      const step = steps[prevStep];
      navigate(step.route);
      scrollToSection(step.scrollTo);
    }
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
    
    // Navigate to the route and scroll to section
    const step = steps[index];
    navigate(step.route);
    scrollToSection(step.scrollTo);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-gray-900/70 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex justify-between items-start sm:items-center p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">How to Use iNVESTT</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Your complete guide to financial goal planning</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors bg-white p-2 rounded-full hover:bg-gray-100 flex-shrink-0 ml-2"
          >
            <SafeIcon icon={FiX} className="text-lg sm:text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-8 flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col"
            >
              {/* Icon and Title */}
              <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-2xl sm:text-3xl shadow-md flex-shrink-0 ${steps[currentStep].color}`}>
                  <SafeIcon icon={steps[currentStep].icon} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                    {steps[currentStep].title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mt-1">
                    {steps[currentStep].description}
                  </p>
                </div>
              </div>

              {/* Details List */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-3 sm:mb-4">
                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                  <SafeIcon icon={FiInfo} className="mr-2 text-indigo-600 flex-shrink-0" />
                  What to do:
                </h4>
                <ul className="space-y-1.5 sm:space-y-2">
                  {steps[currentStep].details.map((detail, index) => (
                    <li key={index} className="flex items-start space-x-2 text-gray-700 text-xs sm:text-sm">
                      <span className="text-indigo-600 font-bold mt-0.5 flex-shrink-0">•</span>
                      <span className="leading-relaxed">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tip Box */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 rounded-r-lg">
                <div className="flex items-start space-x-2">
                  <span className="text-yellow-600 font-bold text-xs sm:text-sm flex-shrink-0">💡 Pro Tip:</span>
                  <p className="text-yellow-800 text-xs sm:text-sm leading-relaxed">{steps[currentStep].tip}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer / Navigation */}
        <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100">
          {/* Progress Indicators */}
          <div className="flex justify-center space-x-1.5 sm:space-x-2 mb-4 sm:mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 cursor-pointer hover:opacity-80 ${
                  index === currentStep 
                    ? 'w-6 sm:w-8 bg-indigo-600' 
                    : index < currentStep
                    ? 'w-1.5 sm:w-2 bg-indigo-400'
                    : 'w-1.5 sm:w-2 bg-gray-300'
                }`}
                title={`Go to step ${index + 1}`}
              />
            ))}
          </div>

          {/* Step Counter */}
          <div className="text-center mb-3 sm:mb-4">
            <span className="text-xs sm:text-sm text-gray-600">
              Step <span className="font-bold text-indigo-600">{currentStep + 1}</span> of {steps.length}
            </span>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center gap-2 sm:gap-0">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-medium transition-all text-sm ${
                currentStep === 0 
                  ? 'text-gray-300 cursor-not-allowed bg-gray-100' 
                  : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900 bg-white border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiArrowLeft} className="text-base sm:text-lg" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>

            <button
              onClick={handleNext}
              className="flex items-center space-x-1 sm:space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 text-sm font-medium"
            >
              <span>{currentStep === steps.length - 1 ? 'Start Planning!' : 'Next Step'}</span>
              <SafeIcon icon={currentStep === steps.length - 1 ? FiCheck : FiArrowRight} className="text-base sm:text-lg" />
            </button>
          </div>

          {/* Skip Tutorial */}
          <div className="text-center mt-3 sm:mt-4">
            <button
              onClick={onClose}
              className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Skip tutorial and start planning
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Walkthrough;